import { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Plus, CalendarDays, Edit, Trash2, Share2 } from 'lucide-react';
import { toast } from 'sonner';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import usePageTitle from '@/hooks/usePageTitle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_OPTIONS = ['Aguardando', 'Em Progresso', 'Finalizado', 'Cancelado'];

export default function Events({ token, user, onLogout }) {
  usePageTitle('Eventos');
  const [events, setEvents] = useState([]);
  const [calendars, setCalendars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, eventId: null });
  const [formData, setFormData] = useState({
    calendar_id: '',
    title: '',
    date: '',
    time: '',
    duration: '',
    comment: '',
    status: 'Aguardando',
    related_event_ids: [],
    // Reminder fields
    reminder_enabled: false,
    reminder_minutes_before: 60, // Default: 1 hour before
    reminder_email: false,
    reminder_whatsapp: false
  });
  const [shareEmail, setShareEmail] = useState('');
  const [shareReadOnly, setShareReadOnly] = useState(false);
  const [shares, setShares] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [eventsRes, calendarsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/events`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${BACKEND_URL}/api/calendars`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (eventsRes.ok && calendarsRes.ok) {
        const eventsData = await eventsRes.json();
        const calendarsData = await calendarsRes.json();
        setEvents(eventsData);
        setCalendars(calendarsData);
      }
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const payload = {
      ...formData,
      calendar_id: parseInt(formData.calendar_id),
      duration: formData.duration ? parseInt(formData.duration) : null,
      time: formData.time || null
    };

    try {
      const url = editingEvent 
        ? `${BACKEND_URL}/api/events/${editingEvent.id}`
        : `${BACKEND_URL}/api/events`;
      
      const response = await fetch(url, {
        method: editingEvent ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success(editingEvent ? 'Evento atualizado!' : 'Evento criado!');
        setDialogOpen(false);
        resetForm();
        fetchData();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Erro ao salvar');
      }
    } catch (error) {
      toast.error('Erro de conexão');
    }
  };

  const resetForm = () => {
    setFormData({
      calendar_id: '',
      title: '',
      date: '',
      time: '',
      duration: '',
      comment: '',
      status: 'Aguardando',
      related_event_ids: []
    });
    setEditingEvent(null);
  };

  const handleDelete = async (id) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/events/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Evento excluído!');
        fetchData();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Erro ao excluir');
      }
    } catch (error) {
      toast.error('Erro de conexão');
    }
    setDeleteDialog({ open: false, eventId: null });
  };

  const openShareDialog = async (event) => {
    setSelectedEvent(event);
    setShareDialogOpen(true);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/events/${event.id}/shares`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setShares(data);
      }
    } catch (error) {
      toast.error('Erro ao carregar compartilhamentos');
    }
  };

  const handleShare = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/events/${selectedEvent.id}/shares`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ user_email: shareEmail, read_only: shareReadOnly })
      });

      if (response.ok) {
        toast.success('Evento compartilhado!');
        setShareEmail('');
        setShareReadOnly(false);
        openShareDialog(selectedEvent);
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Erro ao compartilhar');
      }
    } catch (error) {
      toast.error('Erro de conexão');
    }
  };

  const handleRemoveShare = async (shareId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/events/${selectedEvent.id}/shares/${shareId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Compartilhamento removido!');
        openShareDialog(selectedEvent);
      }
    } catch (error) {
      toast.error('Erro de conexão');
    }
  };

  const getStatusBadgeClass = (status) => {
    const map = {
      'Aguardando': 'status-aguardando',
      'Em Progresso': 'status-em-progresso',
      'Finalizado': 'status-finalizado'
    };
    return map[status] || 'status-aguardando';
  };

  const formatDate = (dateStr) => {
    try {
      if (!dateStr) return '';
      // Remove any time component (T00:00:00 etc)
      const dateOnly = dateStr.split('T')[0];
      // Parse as YYYY-MM-DD and create local date
      const [year, month, day] = dateOnly.split('-');
      // Create date in local timezone (not UTC)
      const localDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      return format(localDate, 'dd/MM/yyyy', { locale: ptBR });
    } catch (error) {
      console.error('Error formatting date:', dateStr, error);
      return dateStr;
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="page-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1>Eventos</h1>
            <p>Gerencie seus eventos e compromissos</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={resetForm} data-testid="create-event-button">
                <Plus size={18} style={{ marginRight: '0.5rem' }} />
                Novo Evento
              </Button>
            </DialogTrigger>
            <DialogContent style={{ maxWidth: '600px', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }} className="dialog-mobile-responsive">
              <DialogHeader>
                <DialogTitle>{editingEvent ? 'Editar Evento' : 'Novo Evento'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} style={{ overflowY: 'auto', flex: 1, paddingRight: '0.5rem' }}>
                <div style={{ marginBottom: '1rem' }}>
                  <Label htmlFor="calendar">Calendário</Label>
                  <Select value={formData.calendar_id.toString()} onValueChange={(value) => setFormData({ ...formData, calendar_id: value })} required>
                    <SelectTrigger data-testid="event-calendar-select">
                      <SelectValue placeholder="Selecione um calendário" />
                    </SelectTrigger>
                    <SelectContent>
                      {calendars.filter(c => !c.read_only).map((cal) => (
                        <SelectItem key={cal.id} value={cal.id.toString()}>{cal.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div style={{ marginBottom: '1rem' }}>
                  <Label htmlFor="title">Título</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                    data-testid="event-title-input"
                  />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                  <div>
                    <Label htmlFor="date">Data</Label>
                    <Input
                      id="date"
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      required
                      data-testid="event-date-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="time">Horário (opcional)</Label>
                    <Input
                      id="time"
                      type="time"
                      value={formData.time}
                      onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                      data-testid="event-time-input"
                    />
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                  <div>
                    <Label htmlFor="duration">Duração (minutos)</Label>
                    <Input
                      id="duration"
                      type="number"
                      value={formData.duration}
                      onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                      data-testid="event-duration-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="status">Status</Label>
                    <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                      <SelectTrigger data-testid="event-status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUS_OPTIONS.map((status) => (
                          <SelectItem key={status} value={status}>{status}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div style={{ marginBottom: '1rem' }}>
                  <Label htmlFor="comment">Comentário</Label>
                  <Textarea
                    id="comment"
                    value={formData.comment}
                    onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
                    data-testid="event-comment-input"
                  />
                </div>
                
                {/* Reminder Section - Only show if time is filled */}
                {formData.time && (
                  <div style={{ 
                    marginBottom: '1rem', 
                    padding: '1rem', 
                    border: '2px solid #e2e8f0', 
                    borderRadius: '8px',
                    background: '#f8fafc'
                  }}>
                    <div style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <input
                        type="checkbox"
                        id="reminder_enabled"
                        checked={formData.reminder_enabled}
                        onChange={(e) => setFormData({ ...formData, reminder_enabled: e.target.checked })}
                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                      />
                      <Label htmlFor="reminder_enabled" style={{ fontWeight: 600, fontSize: '1rem', cursor: 'pointer' }}>
                        🔔 Ativar Lembrete
                      </Label>
                    </div>
                    
                    {formData.reminder_enabled && (
                      <>
                        <div style={{ marginBottom: '1rem' }}>
                          <Label htmlFor="reminder_minutes_before">Avisar quanto tempo antes?</Label>
                          <Select 
                            value={formData.reminder_minutes_before?.toString()} 
                            onValueChange={(value) => setFormData({ ...formData, reminder_minutes_before: parseInt(value) })}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="15">15 minutos antes</SelectItem>
                              <SelectItem value="30">30 minutos antes</SelectItem>
                              <SelectItem value="60">1 hora antes</SelectItem>
                              <SelectItem value="120">2 horas antes</SelectItem>
                              <SelectItem value="360">6 horas antes</SelectItem>
                              <SelectItem value="720">12 horas antes</SelectItem>
                              <SelectItem value="1440">1 dia antes</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        
                        <div style={{ marginBottom: '0.5rem' }}>
                          <Label>Enviar lembrete por:</Label>
                        </div>
                        
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <input
                              type="checkbox"
                              id="reminder_email"
                              checked={formData.reminder_email}
                              onChange={(e) => setFormData({ ...formData, reminder_email: e.target.checked })}
                              style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                            />
                            <Label htmlFor="reminder_email" style={{ cursor: 'pointer', fontSize: '0.9rem' }}>
                              📧 Email
                            </Label>
                          </div>
                          
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <input
                              type="checkbox"
                              id="reminder_whatsapp"
                              checked={formData.reminder_whatsapp}
                              onChange={(e) => setFormData({ ...formData, reminder_whatsapp: e.target.checked })}
                              style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                            />
                            <Label htmlFor="reminder_whatsapp" style={{ cursor: 'pointer', fontSize: '0.9rem' }}>
                              💬 WhatsApp
                            </Label>
                          </div>
                        </div>
                        
                        {!formData.reminder_email && !formData.reminder_whatsapp && (
                          <p style={{ fontSize: '0.8rem', color: '#ef4444', marginTop: '0.5rem' }}>
                            ⚠️ Selecione pelo menos um canal de envio
                          </p>
                        )}
                      </>
                    )}
                  </div>
                )}
                
                {!formData.time && (
                  <p style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '1rem', fontStyle: 'italic' }}>
                    💡 Preencha o horário para ativar lembretes
                  </p>
                )}
                
                <Button type="submit" data-testid="save-event-button">Salvar</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {loading ? (
        <div>Carregando...</div>
      ) : events.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '4rem', color: '#64748b' }}>
          <CalendarDays size={64} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
          <p>Nenhum evento encontrado. Crie seu primeiro evento!</p>
        </div>
      ) : (
        <div className="event-list">
          {/* Agrupar eventos por calendário */}
          {(() => {
            const groupedEvents = events.reduce((acc, event) => {
              const calendarId = event.calendar_id;
              if (!acc[calendarId]) {
                acc[calendarId] = {
                  calendar_name: event.calendar_name,
                  events: []
                };
              }
              acc[calendarId].events.push(event);
              return acc;
            }, {});

            return Object.entries(groupedEvents).map(([calendarId, { calendar_name, events: calendarEvents }]) => (
              <div key={calendarId} style={{ marginBottom: '2rem' }}>
                {/* Header do calendário */}
                <div style={{ 
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  padding: '1rem 1.5rem',
                  borderRadius: '12px 12px 0 0',
                  marginBottom: '0',
                  boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)'
                }}>
                  <h2 style={{ fontSize: '1.3rem', fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <CalendarDays size={24} />
                    {calendar_name}
                  </h2>
                  <p style={{ fontSize: '0.85rem', margin: '0.25rem 0 0 0', opacity: 0.9 }}>
                    {calendarEvents.length} {calendarEvents.length === 1 ? 'evento' : 'eventos'}
                  </p>
                </div>

                {/* Eventos do calendário */}
                <div style={{ 
                  border: '1px solid #e2e8f0',
                  borderTop: 'none',
                  borderRadius: '0 0 12px 12px',
                  overflow: 'hidden'
                }}>
                  {calendarEvents.map((event, index) => (
                    <div 
                      key={event.id} 
                      className="event-card" 
                      data-testid={`event-card-${event.id}`}
                      style={{
                        borderRadius: 0,
                        borderBottom: index < calendarEvents.length - 1 ? '1px solid #e2e8f0' : 'none',
                        margin: 0
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '1rem' }}>
                        <div style={{ flex: 1 }}>
                          <h3 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '0.5rem' }}>{event.title}</h3>
                          <div style={{ fontSize: '0.85rem', color: '#64748b' }}>
                            {formatDate(event.date)}
                            {event.time && ` às ${event.time.slice(0, 5)}`}
                            {!event.is_creator && (
                              <span style={{ marginLeft: '0.5rem', color: '#8b5cf6', fontWeight: 500 }}>
                                • 👤 {event.creator_name}
                              </span>
                            )}
                          </div>
                        </div>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <Badge className={`status-badge ${getStatusBadgeClass(event.status)}`}>
                    {event.status}
                  </Badge>
                  {event.is_creator && (
                    <>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setEditingEvent(event);
                          // Ensure date is in YYYY-MM-DD format (remove any time component)
                          const dateOnly = event.date.split('T')[0];
                          setFormData({
                            calendar_id: event.calendar_id,
                            title: event.title,
                            date: dateOnly,
                            time: event.time || '',
                            duration: event.duration || '',
                            comment: event.comment || '',
                            status: event.status,
                            related_event_ids: event.related_events.map(e => e.id)
                          });
                          setDialogOpen(true);
                        }}
                        data-testid={`edit-event-${event.id}`}
                      >
                        <Edit size={16} />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openShareDialog(event)}
                        data-testid={`share-event-${event.id}`}
                      >
                        <Share2 size={16} />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setDeleteDialog({ open: true, eventId: event.id })}
                        data-testid={`delete-event-${event.id}`}
                        style={{ color: '#ef4444' }}
                      >
                        <Trash2 size={16} />
                      </Button>
                    </>
                  )}
                </div>
              </div>

              {event.comment && (
                <p style={{ color: '#64748b', marginBottom: '1rem', fontSize: '0.9rem' }}>{event.comment}</p>
              )}

              {event.duration && (
                <div style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '0.5rem' }}>
                  Duração: {event.duration} minutos
                </div>
              )}

              {event.related_events && event.related_events.length > 0 && (
                <div style={{ marginTop: '1rem', padding: '0.75rem', background: '#f8fafc', borderRadius: '8px' }}>
                  <div style={{ fontWeight: 500, fontSize: '0.9rem', marginBottom: '0.5rem' }}>Eventos Relacionados:</div>
                  {event.related_events.map((rel) => (
                    <div key={rel.id} style={{ fontSize: '0.85rem', color: '#64748b', marginLeft: '0.5rem' }}>
                      • {rel.title} ({formatDate(rel.date)}) - {rel.status}
                    </div>
                  ))}
                </div>
              )}
                    </div>
                  ))}
                </div>
              </div>
            ));
          })()}
        </div>
      )}

      <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Compartilhar Evento</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleShare} style={{ marginBottom: '1.5rem' }}>
            <div style={{ marginBottom: '1rem' }}>
              <Label htmlFor="shareEmail">Email do usuário</Label>
              <Input
                id="shareEmail"
                type="email"
                value={shareEmail}
                onChange={(e) => setShareEmail(e.target.value)}
                required
                data-testid="share-event-email-input"
              />
            </div>
            <div style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input
                type="checkbox"
                id="shareReadOnly"
                checked={shareReadOnly}
                onChange={(e) => setShareReadOnly(e.target.checked)}
                data-testid="share-event-readonly-checkbox"
              />
              <Label htmlFor="shareReadOnly">Somente leitura</Label>
            </div>
            <Button type="submit" data-testid="share-event-submit-button">Compartilhar</Button>
          </form>

          <div>
            <h4 style={{ marginBottom: '1rem', fontWeight: 600 }}>Compartilhado com:</h4>
            {shares.length === 0 ? (
              <p style={{ color: '#64748b', fontSize: '0.9rem' }}>Ainda não compartilhado</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {shares.map((share) => (
                  <div key={share.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem', background: '#f8fafc', borderRadius: '8px' }}>
                    <div>
                      <div style={{ fontWeight: 500 }}>{share.user_name}</div>
                      <div style={{ fontSize: '0.85rem', color: '#64748b' }}>{share.user_email}</div>
                      {share.read_only && <Badge variant="secondary" style={{ marginTop: '0.25rem' }}>Somente Leitura</Badge>}
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleRemoveShare(share.id)}
                      style={{ color: '#ef4444' }}
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, eventId: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir este evento? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleDelete(deleteDialog.eventId)} style={{ background: '#ef4444' }}>
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
